import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { stringToColor } from "@/shared/utils/color/string-to-color";

/** Static assets live under `public/mockups` (git-ignored, served at `/mockups`). */
const MOCKUPS_BASE = "/mockups";
/** How far (ms) the cursor may be from a detection frame before boxes hide. */
const FRAME_TOLERANCE_MS = 700;

interface Manifest {
  devices: { id: string; clips: string[] }[];
}

interface DetectionBox {
  label: string;
  confidence: number;
  bounding_box: { xmin: number; ymin: number; xmax: number; ymax: number };
}

interface Frame {
  t: number; // absolute ms
  boxes: DetectionBox[];
}

const clipUrl = (device: string, clip: string, file: string) =>
  `${MOCKUPS_BASE}/${encodeURIComponent(device)}/${encodeURIComponent(clip)}/${file}`;

/** Nearest frame index to `t` (frames sorted ascending by `t`), via binary search. */
const nearestFrameIndex = (frames: Frame[], t: number): number => {
  if (frames.length === 0) return -1;
  let lo = 0;
  let hi = frames.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (frames[mid].t < t) lo = mid + 1;
    else hi = mid;
  }
  // lo is the first frame >= t; compare with its predecessor.
  if (lo > 0 && Math.abs(frames[lo - 1].t - t) <= Math.abs(frames[lo].t - t)) return lo - 1;
  return lo;
};

const Dropdown = ({
  label,
  value,
  options,
  onChange,
  format,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
  format?: (v: string) => string;
}) => (
  <label className="flex flex-col gap-1 text-xs">
    <span className="font-medium text-muted-foreground">{label}</span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={options.length === 0}
      className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-xs outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
    >
      {options.map((o) => (
        <option key={o} value={o}>
          {format ? format(o) : o}
        </option>
      ))}
    </select>
  </label>
);

/**
 * "Video (mockup)" detection-replay panel. Plays a local clip from
 * `public/mockups/<device>/<clip>/video.mp4` and overlays the AI bounding boxes
 * from `detections.json`, synced to playback via each frame's absolute timestamp
 * (aligned to `video_data.json`'s `start`). Device + clip dropdowns switch folders.
 */
const MapVideoMockupPanel = () => {
  const { t } = useTranslation("map");
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [manifestError, setManifestError] = useState(false);
  const [device, setDevice] = useState("");
  const [clip, setClip] = useState("");

  const [frames, setFrames] = useState<Frame[]>([]);
  const [startMs, setStartMs] = useState<number | null>(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [aspect, setAspect] = useState(16 / 9);

  const videoRef = useRef<HTMLVideoElement>(null);
  const rafRef = useRef<number | null>(null);

  // Load the manifest once.
  useEffect(() => {
    let cancelled = false;
    fetch(`${MOCKUPS_BASE}/manifest.json`)
      .then((r) => {
        if (!r.ok) throw new Error(String(r.status));
        return r.json();
      })
      .then((m: Manifest) => {
        if (cancelled) return;
        setManifest(m);
        const d = m.devices[0];
        if (d) {
          setDevice(d.id);
          setClip(d.clips[0] ?? "");
        }
      })
      .catch(() => !cancelled && setManifestError(true));
    return () => {
      cancelled = true;
    };
  }, []);

  const clips = useMemo(
    () => manifest?.devices.find((d) => d.id === device)?.clips ?? [],
    [manifest, device],
  );

  const handleDeviceChange = (id: string) => {
    setDevice(id);
    const first = manifest?.devices.find((d) => d.id === id)?.clips[0] ?? "";
    setClip(first);
  };

  // Load the selected clip's detections + time window.
  useEffect(() => {
    if (!device || !clip) return;
    let cancelled = false;
    setFrames([]);
    setStartMs(null);
    setActiveIndex(-1);

    Promise.all([
      fetch(clipUrl(device, clip, "video_data.json")).then((r) => r.json()),
      fetch(clipUrl(device, clip, "detections.json")).then((r) => r.json()),
    ])
      .then(([meta, det]: [{ start: string }, { detections: { timestamp: string; detections: DetectionBox[] }[] }]) => {
        if (cancelled) return;
        setStartMs(Date.parse(meta.start));
        const next: Frame[] = (det.detections ?? [])
          .map((f) => ({ t: Date.parse(f.timestamp), boxes: f.detections ?? [] }))
          .filter((f) => !Number.isNaN(f.t))
          .sort((a, b) => a.t - b.t);
        setFrames(next);
      })
      .catch(() => {
        if (!cancelled) {
          setFrames([]);
          setStartMs(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [device, clip]);

  // Drive the overlay from the video clock (rAF while playing, plus seeks).
  const syncToVideo = useCallback(() => {
    const video = videoRef.current;
    if (!video || startMs == null || frames.length === 0) return;
    const abs = startMs + video.currentTime * 1000;
    const idx = nearestFrameIndex(frames, abs);
    const within = idx >= 0 && Math.abs(frames[idx].t - abs) <= FRAME_TOLERANCE_MS;
    setActiveIndex((prev) => (within ? (prev === idx ? prev : idx) : prev === -1 ? prev : -1));
  }, [frames, startMs]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const loop = () => {
      syncToVideo();
      rafRef.current = requestAnimationFrame(loop);
    };
    const start = () => {
      if (rafRef.current == null) rafRef.current = requestAnimationFrame(loop);
    };
    const stop = () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
    const onMeta = () => {
      if (video.videoWidth && video.videoHeight) setAspect(video.videoWidth / video.videoHeight);
      syncToVideo();
    };
    video.addEventListener("play", start);
    video.addEventListener("pause", stop);
    video.addEventListener("ended", stop);
    video.addEventListener("seeked", syncToVideo);
    video.addEventListener("loadedmetadata", onMeta);
    return () => {
      stop();
      video.removeEventListener("play", start);
      video.removeEventListener("pause", stop);
      video.removeEventListener("ended", stop);
      video.removeEventListener("seeked", syncToVideo);
      video.removeEventListener("loadedmetadata", onMeta);
    };
  }, [syncToVideo, device, clip]);

  const activeBoxes = activeIndex >= 0 ? frames[activeIndex]?.boxes ?? [] : [];

  if (manifestError || (manifest && manifest.devices.length === 0)) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">
        {t("videoMockup.noData")}
        <div className="mt-1 text-xs">
          <Trans
            t={t}
            i18nKey="videoMockup.noDataHint"
            components={{
              path: <code className="rounded bg-muted px-1" />,
              file: <code className="rounded bg-muted px-1" />,
            }}
          />
        </div>
      </div>
    );
  }

  if (!manifest) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">{t("videoMockup.loading")}</div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <Dropdown
        label={t("videoMockup.device")}
        value={device}
        options={manifest.devices.map((d) => d.id)}
        onChange={handleDeviceChange}
      />
      <Dropdown label={t("videoMockup.clip")} value={clip} options={clips} onChange={setClip} />

      {/* Video with detection overlay */}
      <div
        className="relative w-full overflow-hidden rounded-lg border bg-black"
        style={{ aspectRatio: String(aspect) }}
      >
        <video
          ref={videoRef}
          key={`${device}/${clip}`}
          src={device && clip ? clipUrl(device, clip, "video.mp4") : undefined}
          controls
          playsInline
          className="h-full w-full object-contain"
        />
        <svg
          className="pointer-events-none absolute inset-0 h-full w-full"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          {activeBoxes.map((b, i) => {
            const { xmin, ymin, xmax, ymax } = b.bounding_box;
            const color = stringToColor(b.label);
            const x = xmin * 100;
            const y = ymin * 100;
            const w = Math.max(0, (xmax - xmin) * 100);
            const h = Math.max(0, (ymax - ymin) * 100);
            return (
              <g key={`${b.label}-${i}`}>
                <rect
                  x={x}
                  y={y}
                  width={w}
                  height={h}
                  fill="none"
                  stroke={color}
                  strokeWidth={2}
                  vectorEffect="non-scaling-stroke"
                  rx={0.5}
                />
                <text
                  x={x + 0.6}
                  y={Math.max(2.6, y - 0.8)}
                  fill={color}
                  fontSize={3.2}
                  fontWeight={700}
                  style={{ paintOrder: "stroke" }}
                  stroke="rgba(0,0,0,0.6)"
                  strokeWidth={0.6}
                >
                  {b.label} {Math.round(b.confidence * 100)}%
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* Detection summary for the current frame */}
      <div className="rounded-lg border bg-card p-2.5 text-xs">
        {activeBoxes.length > 0 ? (
          <div className="flex flex-col gap-1">
            {activeBoxes.map((b, i) => (
              <div key={i} className="flex items-center gap-2">
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-sm"
                  style={{ backgroundColor: stringToColor(b.label) }}
                />
                <span className="font-medium">{b.label}</span>
                <span className="ml-auto font-mono text-muted-foreground">
                  {Math.round(b.confidence * 100)}%
                </span>
              </div>
            ))}
          </div>
        ) : (
          <span className="text-muted-foreground">
            {frames.length === 0
              ? t("videoMockup.loadingDetections")
              : t("videoMockup.noDetection")}
          </span>
        )}
      </div>
    </div>
  );
};

export default MapVideoMockupPanel;
